/**
 * Acha no texto do e-mail o valor que a leitura extraiu e o marca, para quem confere ver de onde ele saiu.
 *
 * O banco guarda só o valor, não o trecho; então a busca é aqui, tolerante ao jeito de escrever: sem
 * diferença de maiúscula, acento e pontuação no modo texto, e só pelos dígitos no modo dígitos (CPF, telefone,
 * matrícula, protocolo, data), onde `123.456.789-00` e `12345678900` são o mesmo valor.
 */

export type ModoDaBusca = 'texto' | 'digitos';

export interface BuscaNoEmail {
  readonly valor: string;
  readonly modo: ModoDaBusca;
}

export const CLASSE_DO_TRECHO = 'anery-trecho';

/** Abaixo disso o valor casa com pedaço de palavra qualquer e a marca engana mais do que ajuda. */
const MINIMO_DE_CARACTERES: Readonly<Record<ModoDaBusca, number>> = { texto: 3, digitos: 4 };

/** No modo dígitos, o que costuma separar grupos de um MESMO número; qualquer outra coisa encerra o número. */
const SEPARADORES_DE_NUMERO = /[.\-/() ]/;

const SEPARADOR = ' ';

/** O que um caractere vira na comparação: ele normalizado, um separador, ou nada (some sem separar). */
function normalizarCaractere(caractere: string, modo: ModoDaBusca): string {
  if (modo === 'digitos') {
    if (/\d/.test(caractere)) return caractere;
    return SEPARADORES_DE_NUMERO.test(caractere) ? '' : SEPARADOR;
  }
  const base = caractere.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  return /^[\p{L}\p{N}]+$/u.test(base) ? base : SEPARADOR;
}

/** `2026-03-02` (como a leitura grava data) → `02/03/2026` (como o e-mail escreve). */
function valorComoNoEmail(valor: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor.trim());
  return iso === null ? valor : `${iso[3] ?? ''}/${iso[2] ?? ''}/${iso[1] ?? ''}`;
}

export function normalizarParaBusca(valor: string, modo: ModoDaBusca): string {
  let saida = '';
  for (const caractere of valorComoNoEmail(valor)) {
    const normalizado = normalizarCaractere(caractere, modo);
    if (normalizado === SEPARADOR && (saida === '' || saida.endsWith(SEPARADOR))) continue;
    saida += normalizado;
  }
  return saida.trim();
}

interface Posicao {
  readonly no: Text;
  readonly deslocamento: number;
}

/** Tags que ficam dentro da linha; qualquer outra (`td`, `p`, `div`, `br`...) separa o texto de antes do de depois. */
const TAGS_DE_LINHA = new Set([
  'a',
  'abbr',
  'b',
  'code',
  'em',
  'font',
  'i',
  'label',
  'mark',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
]);

interface NoDeTexto {
  readonly no: Text;
  /** Houve quebra (célula, parágrafo, `br`) entre o texto anterior e este: `<td>Hospital</td><td>HOSPITAL X</td>` são duas palavras. */
  readonly depoisDeQuebra: boolean;
}

function nosDeTexto(raiz: Element): NoDeTexto[] {
  const documento = raiz.ownerDocument;
  const caminhante = documento.createTreeWalker(raiz, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (no) => {
      const tag = no.nodeName.toLowerCase();
      return tag === 'script' || tag === 'style' ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });
  const nos: NoDeTexto[] = [];
  let quebra = false;
  for (let no = caminhante.nextNode(); no !== null; no = caminhante.nextNode()) {
    if (no.nodeType === Node.TEXT_NODE) {
      nos.push({ no: no as Text, depoisDeQuebra: quebra });
      quebra = false;
    } else if (!TAGS_DE_LINHA.has(no.nodeName.toLowerCase())) {
      quebra = true;
    }
  }
  return nos;
}

/** Desfaz as marcas anteriores, devolvendo o texto como estava. */
export function limparTrechos(raiz: Element): void {
  for (const marca of Array.from(raiz.querySelectorAll(`mark.${CLASSE_DO_TRECHO}`))) {
    marca.replaceWith(...Array.from(marca.childNodes));
  }
  raiz.normalize();
}

export interface TrechosMarcados {
  /** As marcas na ordem do texto; um trecho que atravessa nós tem mais de uma. */
  readonly marcas: readonly HTMLElement[];
  /** Quantas vezes o valor apareceu inteiro. */
  readonly ocorrencias: number;
  /**
   * Quando o valor inteiro não aparece mas é montado de pedaços (`Rua X, 71, Bairro, Cidade, CEP`, juntado pelo
   * agente numa ordem que o e-mail não tem): quantos pedaços acharam. `null` quando não houve busca por pedaço.
   */
  readonly pedacos: { readonly achados: number; readonly total: number } | null;
}

/** Onde um valor composto se divide: vírgula, ponto e vírgula, barra e ` - `. */
const DIVISORES_DE_PEDACO = /[,;/]|\s[-–]\s/;

function pedacosDoValor(valor: string): string[] {
  const pedacos = valor
    .split(DIVISORES_DE_PEDACO)
    .map((pedaco) => normalizarParaBusca(pedaco, 'texto'))
    .filter((pedaco) => pedaco.length >= MINIMO_DE_CARACTERES.texto);
  return Array.from(new Set(pedacos));
}

/**
 * Marca cada ocorrência do valor dentro de `raiz`. Só casa palavra (ou número) inteira: `ana` não marca dentro
 * de `banana`. Valor de texto que não aparece inteiro é procurado pedaço por pedaço.
 */
export function marcarTrechos(raiz: Element, busca: BuscaNoEmail): TrechosMarcados {
  limparTrechos(raiz);
  const inteiro = marcarAlvo(raiz, normalizarParaBusca(busca.valor, busca.modo), busca.modo);
  const pedacos = busca.modo === 'texto' && inteiro.ocorrencias === 0 ? pedacosDoValor(busca.valor) : [];
  if (pedacos.length < 2) return { ...inteiro, pedacos: null };

  const marcas: HTMLElement[] = [];
  let achados = 0;
  for (const pedaco of pedacos) {
    const resultado = marcarAlvo(raiz, pedaco, 'texto');
    if (resultado.ocorrencias > 0) achados++;
    marcas.push(...resultado.marcas);
  }
  marcas.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  return { marcas, ocorrencias: 0, pedacos: { achados, total: pedacos.length } };
}

/** Marca as ocorrências de um alvo já normalizado, sem desfazer as marcas que já estão lá. */
function marcarAlvo(raiz: Element, alvo: string, modo: ModoDaBusca): Omit<TrechosMarcados, 'pedacos'> {
  if (alvo.length < MINIMO_DE_CARACTERES[modo]) return { marcas: [], ocorrencias: 0 };

  // O texto normalizado do e-mail inteiro, com a origem de cada caractere, para achar e depois voltar ao nó.
  let texto = '';
  const origens: Posicao[] = [];
  for (const { no, depoisDeQuebra } of nosDeTexto(raiz)) {
    if (depoisDeQuebra && texto !== '' && !texto.endsWith(SEPARADOR)) {
      // O separador da quebra não tem caractere de origem; aponta para o começo do nó seguinte.
      texto += SEPARADOR;
      origens.push({ no, deslocamento: 0 });
    }
    const conteudo = no.data;
    for (let i = 0; i < conteudo.length; i++) {
      const normalizado = normalizarCaractere(conteudo.charAt(i), modo);
      if (normalizado === SEPARADOR && (texto === '' || texto.endsWith(SEPARADOR))) continue;
      for (const caractere of normalizado) {
        texto += caractere;
        origens.push({ no, deslocamento: i });
      }
    }
  }

  const trechos: { no: Text; inicio: number; fim: number }[] = [];
  let ocorrencias = 0;
  for (let inicio = texto.indexOf(alvo); inicio !== -1; inicio = texto.indexOf(alvo, inicio + alvo.length)) {
    const fim = inicio + alvo.length;
    const antes = inicio === 0 ? SEPARADOR : texto.charAt(inicio - 1);
    const depois = fim >= texto.length ? SEPARADOR : texto.charAt(fim);
    if (antes !== SEPARADOR || depois !== SEPARADOR) continue;

    // Um trecho pode atravessar nós (`<b>Hospital</b> Central`): uma marca por pedaço de nó.
    const porNo = new Map<Text, { inicio: number; fim: number }>();
    for (let i = inicio; i < fim; i++) {
      const origem = origens[i];
      if (origem === undefined) continue;
      const atual = porNo.get(origem.no);
      porNo.set(origem.no, {
        inicio: Math.min(atual?.inicio ?? origem.deslocamento, origem.deslocamento),
        fim: Math.max(atual?.fim ?? 0, origem.deslocamento + 1),
      });
    }
    for (const [no, faixa] of porNo) trechos.push({ no, ...faixa });
    ocorrencias++;
  }

  // Do fim para o começo dentro de cada nó: `splitText` mantém o começo no nó original, então os trechos
  // anteriores continuam apontando para o lugar certo.
  const ordemDosNos = new Map(nosDeTexto(raiz).map(({ no }, i) => [no, i]));
  trechos.sort((a, b) => (ordemDosNos.get(a.no) ?? 0) - (ordemDosNos.get(b.no) ?? 0) || b.inicio - a.inicio);

  const marcas: { marca: HTMLElement; no: number; inicio: number }[] = [];
  for (const trecho of trechos) {
    const meio = trecho.no.splitText(trecho.inicio);
    meio.splitText(trecho.fim - trecho.inicio);
    const marca = raiz.ownerDocument.createElement('mark');
    marca.className = CLASSE_DO_TRECHO;
    meio.replaceWith(marca);
    marca.appendChild(meio);
    marcas.push({ marca, no: ordemDosNos.get(trecho.no) ?? 0, inicio: trecho.inicio });
  }
  return {
    marcas: marcas.sort((a, b) => a.no - b.no || a.inicio - b.inicio).map((m) => m.marca),
    ocorrencias,
  };
}
